import type { ExpertReportResult } from './resultFormat';

/**
 * Поля записи блока (или итога) заключения, по которым читается его результат: полученное
 * системой и выставленное пользователем стоят в записи рядом, а действует из них одно.
 */
export type EvaluationResultSource = {
  rating?: number | null;
  maxRating?: number | null;
  value?: string | null;
  values?: string[] | null;
  /** Числовая часть шкалы блока; приходит у блока, шкала которого несёт и баллы, и справочник */
  possiblePoints?: number[] | null;
  manualRating?: number | null;
  manualValue?: string | null;
  unit?: string | null;
};

/**
 * Потолок балла блока: максимум, с которым блок оценён, а без него — наибольший из баллов его
 * шкалы. Второе — случай блока с совмещённой шкалой, ответившего значением: своего максимума у
 * такого результата нет, и границей правки баллом остаются возможные баллы шкалы.
 */
export const getEvaluationMaxScore = ({ maxRating, possiblePoints }: EvaluationResultSource): number | null =>
  maxRating ?? (possiblePoints?.length ? Math.max(...possiblePoints) : null);

/** Результат, полученный системой: балл со своим максимумом, значение справочника либо набор значений */
export const getSystemEvaluationResult = (source: EvaluationResultSource): ExpertReportResult => {
  if (source.value != null) return { value: source.value };
  if (source.values?.length) return { values: source.values };
  if (source.rating != null)
    return { score: source.rating, maxScore: getEvaluationMaxScore(source), unit: source.unit };
  return {};
};

/**
 * Действующий результат: выставленный пользователем старше полученного системой, в какой бы форме
 * каждый из них ни был, — так его читает и сама запись результата на сервере. Результат-значение
 * ни балла, ни максимума за собой не несёт, даже когда баллы шкалой блока предусмотрены.
 */
export const getEffectiveEvaluationResult = (source: EvaluationResultSource): ExpertReportResult => {
  if (source.manualValue != null) return { value: source.manualValue };
  if (source.manualRating != null)
    return { score: source.manualRating, maxScore: getEvaluationMaxScore(source), unit: source.unit };
  return getSystemEvaluationResult(source);
};

/**
 * Шкала блока несёт баллы — значит, его результат правится и вводом балла. У блока со справочником
 * значений баллов может не быть вовсе: тогда правка баллом ему не предлагается.
 */
export const hasPointsScale = (source: EvaluationResultSource): boolean =>
  Boolean(source.possiblePoints?.length) || source.maxRating != null || source.rating != null;
